/**
 * Map error handling utilities.
 *
 * Provides safe wrappers around map operations that might throw,
 * especially during WebGL context loss or invalid geometries.
 */

// ─── Error types ────────────────────────────────────────────────────

export type MapErrorType =
  | 'webgl_context_lost'
  | 'invalid_geometry'
  | 'token_missing'
  | 'tile_load_failed'
  | 'network_error'
  | 'rate_limited'
  | 'unknown';

export interface MapError {
  type: MapErrorType;
  message: string;
  recoverable: boolean;
  retryable: boolean;
}

// ─── Error factories ────────────────────────────────────────────────

export function webglContextLostError(): MapError {
  return {
    type: 'webgl_context_lost',
    message: 'WebGL context was lost. The map may need to be reloaded.',
    recoverable: true,
    retryable: true,
  };
}

export function invalidGeometryError(detail: string): MapError {
  return {
    type: 'invalid_geometry',
    message: `Invalid geometry: ${detail}`,
    recoverable: true,
    retryable: false,
  };
}

export function tokenMissingError(): MapError {
  return {
    type: 'token_missing',
    message: 'Mapbox access token is not configured. Map features are unavailable.',
    recoverable: false,
    retryable: false,
  };
}

export function tileLoadError(url: string): MapError {
  return {
    type: 'tile_load_failed',
    message: `Failed to load map tiles from ${new URL(url).hostname}`,
    recoverable: true,
    retryable: true,
  };
}

export function networkError(detail: string): MapError {
  return {
    type: 'network_error',
    message: `Network error: ${detail}`,
    recoverable: true,
    retryable: true,
  };
}

export function rateLimitedError(): MapError {
  return {
    type: 'rate_limited',
    message: 'Too many requests. Please wait a moment before trying again.',
    recoverable: true,
    retryable: true,
  };
}

// ─── Safe execution helpers ─────────────────────────────────────────

/**
 * Execute a function and catch map-related errors.
 * Returns null on failure and logs the error.
 */
export function safeMapOperation<T>(operation: () => T, context: string): T | null {
  try {
    return operation();
  } catch (error) {
    console.error(`[Map] ${context}:`, error);
    return null;
  }
}

/**
 * Classify an unknown error into a MapError.
 */
export function classifyError(error: unknown): MapError {
  if (error && typeof error === 'object' && 'type' in error) {
    return error as MapError;
  }

  const msg = error instanceof Error ? error.message : String(error);

  if (msg.includes('webgl') || msg.includes('WebGL') || msg.includes('context lost')) {
    return webglContextLostError();
  }
  if (msg.includes('token') || msg.includes('Token')) {
    return tokenMissingError();
  }
  if (msg.includes('network') || msg.includes('Network') || msg.includes('fetch')) {
    return networkError(msg);
  }
  if (msg.includes('429') || msg.includes('rate limit')) {
    return rateLimitedError();
  }

  return {
    type: 'unknown',
    message: msg,
    recoverable: false,
    retryable: false,
  };
}

// ─── Feature count limits ──────────────────────────────────────────

/**
 * Maximum number of features to render in a single layer.
 * Beyond this, we aggregate or paginate.
 */
export const MAX_RENDER_FEATURES = 5000;

/**
 * Check if a feature set exceeds the safe rendering limit.
 */
export function exceedsRenderLimit(count: number): boolean {
  return count > MAX_RENDER_FEATURES;
}

/**
 * Suggest a zoom-based feature limit.
 * At low zooms we show fewer features, at high zooms more.
 */
export function featuresForZoom(zoom: number): number {
  if (zoom < 8) return 200;
  if (zoom < 10) return 500;
  if (zoom < 12) return 1000;
  if (zoom < 14) return 2000;
  return MAX_RENDER_FEATURES;
}
